import React from "react";
import { Bookmark, BookmarkCheck } from "lucide-react";
import { useBookmarks } from "../hooks/useBookmarks";
import { trackBookmark } from "../lib/analytics";

/**
 * Tiny, calm bookmark control. No labels, no counts, no animation noise.
 * Used on TopicCard, SourceVault, Timeline event panel, EntityPage hero.
 *
 * Props:
 *   type: 'topic' | 'source' | 'event' | 'entity'
 *   id: stable identifier
 *   title: human-readable label kept in the bookmark for the /saved page
 *   subtitle: optional short context line
 *   meta: optional small payload (e.g. {category, date}) used by /saved
 *   href: canonical URL used when re-opening from /saved
 *   size: 'sm' | 'md'
 *   stopPropagation: prevent click bubbling (when nested inside a card link)
 */
export default function BookmarkButton({
    type,
    id,
    title,
    subtitle,
    meta,
    href,
    size = "sm",
    stopPropagation = true,
    className = "",
}) {
    const { isBookmarked, toggle } = useBookmarks();
    if (!id) return null;
    const saved = isBookmarked(type, id);
    const Icon = saved ? BookmarkCheck : Bookmark;
    const sz = size === "md" ? "w-4 h-4" : "w-3.5 h-3.5";

    const onClick = (e) => {
        if (stopPropagation) {
            e.preventDefault();
            e.stopPropagation();
        }
        const added = toggle({ type, id, title, subtitle, meta, href });
        trackBookmark(added ? "add" : "remove", type, id);
    };

    return (
        <button
            type="button"
            data-testid={`bookmark-${type}-${id}`}
            data-bookmarked={saved}
            onClick={onClick}
            aria-label={saved ? `Remove ${title} from saved` : `Save ${title}`}
            title={saved ? "Saved · click to remove" : "Save for later"}
            className={`inline-flex items-center justify-center rounded-md p-1.5 transition-colors ${saved ? "text-[#56CCF2] hover:text-white" : "text-[#64748B] hover:text-[#56CCF2]"} ${className}`}
        >
            <Icon className={sz} />
        </button>
    );
}
